import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { gzip } from 'pako';
import { WizCommand } from './wiz-command';
import { WIZ_COMMAND_HTTP_METHOD } from '@wizeoo/akm-core-lib';

/**
 * Command manager
 * statique
 * @dynamic
 */
export class WizCommandManager {
  /**
   * Command call pile
   * Each call will be registered (source observable to fill)
   * key = Command UUID, value = the command
   */
  private static lockPile: Map<string, WizCommand<any>> = new Map();

  /** Global lock flag for external check */
  public static lock: BehaviorSubject<boolean> = new BehaviorSubject(false);

  /** alimentation pour requêtes HTTP */
  constructor() {}

  /** Http client accessor, supplied by WizModule */
  public static httpClient: HttpClient = null;

  /**
   * Add/remove command to/from pile
   * @param pCall: the WizCommand
   * @param pAdd: Adding or removal (add =true, delete = false)
   */
  public static modifyLockPile(pCall: WizCommand<any>, pAdd: boolean) {
    if (pAdd) {
      WizCommandManager.lockPile.set(pCall.uuid, pCall);
    } else {
      WizCommandManager.lockPile.delete(pCall.uuid);
    }
    WizCommandManager.manageLock();
  }

  /** Lock flag managing method */
  private static manageLock() {
    const pileSize: number = WizCommandManager.lockPile.size;
    if (pileSize > 0) {
      WizCommandManager.setLock(true);
    } else {
      WizCommandManager.setLock(false);
    }
  }

  /**
   * Fills lock flag
   * @param value : boolean
   */
  public static setLock(value: boolean) {
    WizCommandManager.lock.next(value);
  }

  /**
   *  Command execution.
   * Will fill Result or Fault on the command
   * @param cmd: WizCommand
   * @return : void.
   */
  public static async executeCommand(cmd: WizCommand<any>): Promise<any> {
    // Lock
    if (cmd.doLock) {
      WizCommandManager.modifyLockPile(cmd, true);
    }

    if (cmd.isRemote) {
      // request preparation
      // TODO méthode dynamique
      if (cmd.httpMethod === WIZ_COMMAND_HTTP_METHOD.POST) {
        let body = cmd.context;
        let headers = new HttpHeaders();
        // Si la requête doit être compressée, on le fait et on l'indique
        // en utilisant le header : Content-Encoding = gzip
        if (cmd.toZip) {
          body = gzip(JSON.stringify(body)).buffer;
          headers = headers.append('Content-Encoding','gzip');
        }
        return WizCommandManager.httpClient
          .post<any[]>(cmd.url, body, { headers: headers })
          .toPromise()
          .then(
            (response) => this.commandEnd(cmd, response),
            (error) => this.commandReject(cmd, error)
          );
      } else if (cmd.httpMethod === WIZ_COMMAND_HTTP_METHOD.PUT) {
        return WizCommandManager.httpClient
          .put<any[]>(cmd.url, cmd.context)
          .toPromise()
          .then(
            (response) => this.commandEnd(cmd, response),
            (error) => this.commandReject(cmd, error)
          );
      } else if (cmd.httpMethod === WIZ_COMMAND_HTTP_METHOD.DELETE) {
        return WizCommandManager.httpClient
          .delete<any[]>(cmd.url, cmd.context || {})
          .toPromise()
          .then(
            (response) => this.commandEnd(cmd, response),
            (error) => this.commandReject(cmd, error)
          );
      } else {
        return WizCommandManager.httpClient
          .get<any[]>(cmd.url, cmd.context || {})
          .toPromise()
          .then(
            (response) => this.commandEnd(cmd, response),
            (error) => this.commandReject(cmd, error)
          );
      }
    } else {
      // local command
      return cmd.wizExecute().then(
        (result) => this.commandEnd(cmd, result),
        (error) => this.commandReject(cmd, error)
      );
    }
  }

  /**
   * Command result handler
   * @param cmd Executed command
   * @param result Call result
   */
  static commandEnd(cmd: WizCommand<any>, result: any) {
    if (cmd.doLock) {
      WizCommandManager.modifyLockPile(cmd, false);
    }

    if(!result && !!cmd.isRemote) {
      console.log('wizCmdManager', cmd, WizCommandManager.httpClient);
    }

    cmd.wizCallResult(result);
  }

  /**
   * Command error handler
   * @param cmd Executed command
   * @param result Call error
   */
  static commandReject(cmd: WizCommand<any>, error: Error) {
    if (cmd.doLock) {
      WizCommandManager.modifyLockPile(cmd, false);
    }
    cmd.wizCallFault(error);
  }
}
